import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../utils/cn';
import {
  Bot,
  Plus,
  Play,
  Pause,
  Settings,
  Trash2,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Activity,
  Clock,
  Target,
  BarChart3,
  Zap,
  Shield,
  ExternalLink,
  ChevronDown,
  MoreVertical,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Circle,
  Grid3X3,
  Repeat,
  ArrowRightLeft,
  Layers,
  Eye,
  Copy,
  Link2,
} from 'lucide-react';

interface DeployedBot {
  id: string;
  name: string;
  strategy: string;
  strategyIcon: typeof Bot;
  status: 'running' | 'paused' | 'stopped' | 'error';
  network: string;
  pair: string;
  contractAddress?: string;
  pnl: number;
  pnlPercent: number;
  totalTrades: number;
  winRate: number;
  capital: number;
  currentValue: number;
  runtime: string;
  lastTrade: string;
  gasSpent: number;
}

// Empty state - bots data comes from API
const emptyBots: DeployedBot[] = [];

const statusConfig = {
  running: { label: 'Running', color: '#0ecb81', icon: CheckCircle },
  paused: { label: 'Paused', color: '#f0b90b', icon: Pause },
  stopped: { label: 'Stopped', color: '#848e9c', icon: Circle },
  error: { label: 'Error', color: '#f6465d', icon: AlertTriangle },
};

const networks = ['All Networks', 'Ethereum', 'Arbitrum', 'Base', 'Polygon', 'Solana', 'BSC'];

export default function Bots() {
  const navigate = useNavigate();
  const [bots] = useState<DeployedBot[]>(emptyBots);
  const [selectedNetwork, setSelectedNetwork] = useState('All Networks');
  const [selectedBot, setSelectedBot] = useState<DeployedBot | null>(null);

  const filteredBots = bots.filter(bot => 
    selectedNetwork === 'All Networks' || bot.network === selectedNetwork
  );

  const totalValue = bots.reduce((sum, bot) => sum + bot.currentValue, 0);
  const totalPnl = bots.reduce((sum, bot) => sum + bot.pnl, 0);
  const totalCapital = bots.reduce((sum, bot) => sum + bot.capital, 0);
  const runningBots = bots.filter(bot => bot.status === 'running').length;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1">Deployed Bots</h1>
          <p className="text-[#848e9c]">Manage your smart contract trading bots</p>
        </div>
        <button
          onClick={() => navigate('/strategies')}
          className="btn-primary btn-md"
        >
          <Plus className="h-4 w-4" />
          Deploy New Bot
        </button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="card p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-[#848e9c]">Total Value</span>
            <DollarSign className="h-4 w-4 text-[#f0b90b]" />
          </div>
          <div className="text-2xl font-bold text-white">${totalValue.toLocaleString()}</div>
          <div className={cn(
            "text-sm mt-1",
            totalPnl >= 0 ? "text-[#0ecb81]" : "text-[#f6465d]"
          )}>
            {totalPnl >= 0 ? '+' : ''}${totalPnl.toLocaleString()} ({((totalPnl / totalCapital) * 100).toFixed(1)}%)
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-[#848e9c]">Active Bots</span>
            <Bot className="h-4 w-4 text-[#0ecb81]" />
          </div>
          <div className="text-2xl font-bold text-white">{runningBots}</div>
          <div className="text-sm text-[#5e6673] mt-1">of {bots.length} total</div>
        </div>
        <div className="card p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-[#848e9c]">Total Trades</span>
            <Activity className="h-4 w-4 text-[#1e90ff]" />
          </div>
          <div className="text-2xl font-bold text-white">
            {bots.reduce((sum, b) => sum + b.totalTrades, 0)}
          </div>
          <div className="text-sm text-[#5e6673] mt-1">Across all bots</div>
        </div>
        <div className="card p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-[#848e9c]">Gas Spent</span>
            <Zap className="h-4 w-4 text-[#f0b90b]" />
          </div>
          <div className="text-2xl font-bold text-white">
            ${bots.reduce((sum, b) => sum + b.gasSpent, 0).toFixed(2)}
          </div>
          <div className="text-sm text-[#5e6673] mt-1">Total fees</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          {networks.map((network) => (
            <button
              key={network}
              onClick={() => setSelectedNetwork(network)}
              className={cn(
                "px-3 py-1.5 rounded-lg text-sm font-medium transition-colors",
                selectedNetwork === network
                  ? "bg-[#f0b90b]/10 text-[#f0b90b]"
                  : "text-[#848e9c] hover:text-white hover:bg-white/[0.04]"
              )}
            >
              {network}
            </button>
          ))}
        </div>
      </div>

      {/* Bot Cards */}
      <div className="space-y-4">
        {filteredBots.map((bot) => {
          const StatusIcon = statusConfig[bot.status].icon;
          const StrategyIcon = bot.strategyIcon;
          
          return (
            <div
              key={bot.id}
              className="card p-5 hover:border-white/[0.15] transition-all cursor-pointer"
              onClick={() => setSelectedBot(bot)}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#f0b90b]/10 flex items-center justify-center">
                    <StrategyIcon className="h-6 w-6 text-[#f0b90b]" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-semibold text-white">{bot.name}</h3>
                      <div 
                        className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs"
                        style={{ 
                          backgroundColor: `${statusConfig[bot.status].color}15`,
                          color: statusConfig[bot.status].color 
                        }}
                      >
                        <StatusIcon className="h-3 w-3" />
                        {statusConfig[bot.status].label}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-[#848e9c]">
                      <span>{bot.strategy}</span>
                      <span className="text-[#5e6673]">•</span>
                      <span>{bot.pair}</span>
                      <span className="text-[#5e6673]">•</span>
                      <span className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full bg-[#8b5cf6]" />
                        {bot.network}
                      </span>
                    </div>
                    {bot.contractAddress && (
                      <div className="flex items-center gap-2 mt-2">
                        <code className="text-xs text-[#5e6673] bg-[#12161c] px-2 py-1 rounded">
                          {bot.contractAddress}
                        </code>
                        <button className="text-[#5e6673] hover:text-[#f0b90b]">
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                        <button className="text-[#5e6673] hover:text-[#f0b90b]">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-8">
                  {/* Performance */}
                  <div className="text-right">
                    <div className={cn(
                      "text-lg font-semibold",
                      bot.pnl >= 0 ? "text-[#0ecb81]" : "text-[#f6465d]"
                    )}>
                      {bot.pnl >= 0 ? '+' : ''}${bot.pnl.toLocaleString()}
                    </div>
                    <div className={cn(
                      "text-sm",
                      bot.pnlPercent >= 0 ? "text-[#0ecb81]" : "text-[#f6465d]"
                    )}>
                      {bot.pnlPercent >= 0 ? '+' : ''}{bot.pnlPercent}%
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-6 text-sm">
                    <div className="text-center">
                      <div className="text-[#848e9c]">Trades</div>
                      <div className="font-medium text-white">{bot.totalTrades}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-[#848e9c]">Win Rate</div>
                      <div className="font-medium text-white">{bot.winRate}%</div>
                    </div>
                    <div className="text-center">
                      <div className="text-[#848e9c]">Runtime</div>
                      <div className="font-medium text-white">{bot.runtime}</div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {bot.status === 'running' ? (
                      <button className="p-2 rounded-lg bg-[#f0b90b]/10 text-[#f0b90b] hover:bg-[#f0b90b]/20">
                        <Pause className="h-4 w-4" />
                      </button>
                    ) : (
                      <button className="p-2 rounded-lg bg-[#0ecb81]/10 text-[#0ecb81] hover:bg-[#0ecb81]/20">
                        <Play className="h-4 w-4" />
                      </button>
                    )}
                    <button className="p-2 rounded-lg hover:bg-white/[0.06] text-[#848e9c] hover:text-white">
                      <Settings className="h-4 w-4" />
                    </button>
                    <button className="p-2 rounded-lg hover:bg-white/[0.06] text-[#848e9c] hover:text-white">
                      <MoreVertical className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredBots.length === 0 && (
        <div className="card p-12 text-center">
          <div className="w-16 h-16 rounded-2xl bg-[#1e2329] flex items-center justify-center mx-auto mb-4">
            <Bot className="h-8 w-8 text-[#848e9c]" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">No Bots Found</h3>
          <p className="text-[#848e9c] mb-4">Deploy your first trading bot to get started</p>
          <button
            onClick={() => navigate('/strategies')}
            className="btn-primary btn-md mx-auto"
          >
            <Plus className="h-4 w-4" />
            Deploy New Bot
          </button>
        </div>
      )}

      {/* Bot Detail Modal */}
      {selectedBot && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-6" onClick={() => setSelectedBot(null)}>
          <div className="bg-[#1e2329] rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-white/[0.06]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#f0b90b]/10 flex items-center justify-center">
                    <selectedBot.strategyIcon className="h-6 w-6 text-[#f0b90b]" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">{selectedBot.name}</h2>
                    <p className="text-[#848e9c]">{selectedBot.strategy} • {selectedBot.network}</p>
                  </div>
                </div>
                <div 
                  className="flex items-center gap-1.5 px-3 py-1 rounded-full text-sm"
                  style={{ 
                    backgroundColor: `${statusConfig[selectedBot.status].color}15`,
                    color: statusConfig[selectedBot.status].color 
                  }}
                >
                  {statusConfig[selectedBot.status].label}
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Performance Grid */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-[#12161c] rounded-xl p-4">
                  <div className="text-xs text-[#5e6673] mb-1">Current Value</div>
                  <div className="text-xl font-bold text-white">${selectedBot.currentValue.toLocaleString()}</div>
                </div>
                <div className="bg-[#12161c] rounded-xl p-4">
                  <div className="text-xs text-[#5e6673] mb-1">Total P&L</div>
                  <div className={cn(
                    "text-xl font-bold",
                    selectedBot.pnl >= 0 ? "text-[#0ecb81]" : "text-[#f6465d]"
                  )}>
                    {selectedBot.pnl >= 0 ? '+' : ''}${selectedBot.pnl.toLocaleString()}
                  </div>
                </div>
                <div className="bg-[#12161c] rounded-xl p-4">
                  <div className="text-xs text-[#5e6673] mb-1">Win Rate</div>
                  <div className="text-xl font-bold text-white">{selectedBot.winRate}%</div>
                </div>
                <div className="bg-[#12161c] rounded-xl p-4">
                  <div className="text-xs text-[#5e6673] mb-1">Total Trades</div>
                  <div className="text-xl font-bold text-white">{selectedBot.totalTrades}</div>
                </div>
              </div>

              {/* Contract Info */}
              {selectedBot.contractAddress && (
                <div>
                  <h3 className="text-sm font-medium text-white mb-3">Smart Contract</h3>
                  <div className="bg-[#12161c] rounded-xl p-4 flex items-center justify-between">
                    <code className="text-sm text-[#848e9c]">{selectedBot.contractAddress}</code>
                    <div className="flex items-center gap-2">
                      <button className="btn-ghost btn-sm">
                        <Copy className="h-4 w-4" />
                      </button>
                      <button className="btn-ghost btn-sm">
                        <ExternalLink className="h-4 w-4" />
                        View on Explorer
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-white/[0.06]">
                <button onClick={() => navigate(`/workflow/${selectedBot.id}`)} className="flex-1 btn-primary btn-lg">
                  <Eye className="h-4 w-4" />
                  View Strategy
                </button>
                {selectedBot.status === 'running' ? (
                  <button className="btn-secondary btn-lg">
                    <Pause className="h-4 w-4" />
                    Pause Bot
                  </button>
                ) : (
                  <button className="btn-secondary btn-lg">
                    <Play className="h-4 w-4" />
                    Start Bot
                  </button>
                )}
                <button className="btn-ghost btn-lg text-[#f6465d]">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
